import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { updateProvider } from "../../db/Providers";
import { PhoneIcon } from "@heroicons/react/20/solid";
import FormInput from "../../components/controls/FormInput";
import PlacesAutocomplete from "../../components/Places/PlacesAutocomplete";

const ProviderModal = ({ provider, onClose, showButtons }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    companyName: provider.companyName || "",
    neqNumber: provider.neqNumber || "",
    contactPerson: provider.contactPerson || "",
    phoneNumber: provider.phoneNumber || "",
    contactEmail: provider.contactEmail || "",
    website: provider.website || "",
    companyAddress: provider.companyAddress || "",
    priceRange: provider.priceRange || "",
    productsAndServices: provider.productsAndServices || "",
    location: provider.location || null,
    status: provider.status || "",
    userId: provider.userId || "",
    companyId: provider.companyId || 0,
  });

  const handleChange = (name, value) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: name === "location" ? { ...prevFormData[name], ...value } : value,
    }));
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return t("providerRequestsModal.noPhoneNumber");

    const cleaned = ("" + phoneNumber).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phoneNumber;
  };

  const handleAction = async (status) => {
    try {
      const updatedProvider = { ...formData, status };
      await updateProvider(updatedProvider);

      onClose();
    } catch (error) {
      console.error("Erreur :", error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex justify-center items-center z-50 text-onWhite">
      <div className="bg-white p-8 rounded-md max-w-3xl w-full shadow-lg max-h-screen overflow-y-auto">
        <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">
          {t("providerRequestsModal.title")}
        </h2>

        {/* Utilisation du grid pour avoir deux colonnes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label={t("providerRequestsModal.companyName")}
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            placeholder={t("providerRequestsModal.companyNamePlaceholder")}
            required
          />
          <FormInput
            label={t("providerRequestsModal.neqNumber")}
            name="neqNumber"
            value={formData.neqNumber}
            onChange={handleChange}
            placeholder={t("providerRequestsModal.neqNumberPlaceholder")}
          />
          {/* Personne ressource */}
          <FormInput
            label={t("becomeProvider.contactPersonLabel")}
            name="contactPerson"
            value={formData.contactPerson}
            onChange={handleChange}
            placeholder={t("becomeProvider.contactPersonPlaceholder")}
            required
          />

          {/* Numéro de téléphone */}
          <FormInput
            label={t("becomeProvider.phoneNumberLabel")}
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder={t("becomeProvider.phoneNumberPlaceholder")}
            type="tel"
            required
          />
          <FormInput
            label={t("providerRequestsModal.contactEmail")}
            name="contactEmail"
            value={formData.contactEmail}
            onChange={handleChange}
            placeholder={t("providerRequestsModal.contactEmailPlaceholder")}
            type="email"
            required
          />
          <FormInput
            label={t("providerRequestsModal.website")}
            name="website"
            value={formData.website}
            onChange={handleChange}
            placeholder={t("providerRequestsModal.websitePlaceholder")}
          />
          <div>
            <PlacesAutocomplete
              title={t("providerEdit.companyAddress")}
              onSelect={(e) => {
                handleChange("location", e);
                handleChange("companyAddress", e?.formattedAddress || "");
              }}
              initialAddress={formData?.location?.formattedAddress || ""}
              showOptional={false}
            />
          </div>
          <FormInput
            label={t("providerRequestsModal.priceRange")}
            name="priceRange"
            value={formData.priceRange}
            onChange={handleChange}
            placeholder={t("providerRequestsModal.priceRangePlaceholder")}
            required
          />
          <FormInput
            label={t("providerRequestsModal.productsAndServices")}
            name="productsAndServices"
            value={formData.productsAndServices}
            onChange={handleChange}
            placeholder={t(
              "providerRequestsModal.productsAndServicesPlaceholder"
            )}
            required
          />
        </div>

        {/* Boutons */}
        <div className="flex justify-end space-x-4 mt-8">
          <button
            onClick={() => handleAction("")}
            className="bg-green-500 text-white px-5 py-2 rounded-full hover:bg-green-600 transition"
          >
            {t("modal.save")}
          </button>
          {showButtons && (
            <>
              <button
                onClick={() => handleAction("accepted")}
                className="bg-green-500 text-white px-5 py-2 rounded-full hover:bg-green-600 transition"
              >
                {t("providerRequestsModal.approve")}
              </button>
              <button
                onClick={() => handleAction("rejected")}
                className="bg-red-500 text-white px-5 py-2 rounded-full hover:bg-red-600 transition"
              >
                {t("providerRequestsModal.reject")}
              </button>
            </>
          )}
          <button
            onClick={onClose}
            className="bg-gray-500 text-white px-5 py-2 rounded-full hover:bg-gray-600 transition"
          >
            {t("providerRequestsModal.close")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProviderModal;
